import { useCallback, useMemo } from 'react';

import { usePeopleTeamsAnalyticsSubcontext } from '@atlaskit/people-teams-ui-public/analytics';
import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { type ExperienceEvent } from '@atlassian/experience-tracker';

import {
	createTeamsExperienceTracker,
	type UserExperience,
	type UserExperiences,
} from './experiences';
import { type CommonExperienceEventAttributes } from './experiences/types';
import { type EventKey } from './generated/analytics.types';
import { useAnalyticsEvents as useDeprecatedAnalyticsEvents } from './generated/use-analytics-events';
import { useAnalyticsEventsWithTags } from './use-analytics-events-with-tags';

/**
 * @private
 * @deprecated Analytics events should be fired using the `@atlaskit/teams-app-internal-analytics` package.
 */
export const usePeopleTeamAnalyticsEvents = () => {
	const [{ eventAttributes: injectedEventAttributes }, { setEventAttributes }] =
		usePeopleTeamsAnalyticsSubcontext();
	const { fireEvent } = useDeprecatedAnalyticsEvents();

	const fireEventWithSubcontextAttributesInjected = useCallback(
		<K extends EventKey>(...params: Parameters<typeof fireEvent<K>>) => {
			const [event, attributes] = params;
			const eventWithSubcontextAttributes = {
				...attributes,
				...injectedEventAttributes,
			};
			// @ts-ignore allow injecting subcontext attributes
			fireEvent(event, eventWithSubcontextAttributes);
		},
		[fireEvent, injectedEventAttributes],
	);

	return {
		fireEvent: fireEventWithSubcontextAttributesInjected,
		setInjectedEventAttributes: setEventAttributes,
	};
};

/**
 * @private
 * @deprecated Analytics events should be fired using the `@atlaskit/teams-app-internal-analytics` package.
 */
export const usePeopleTeamAnalyticsEventsWithTags = () => useAnalyticsEventsWithTags();

export const useTeamsExperienceTracker = () => {
	const { fireEvent: DEPRECATED_fireEvent } = usePeopleTeamAnalyticsEvents();
	const { fireEvent } = useAnalyticsEvents();

	const fireExperienceEvent = useCallback(
		<T extends UserExperience>(
			action: ExperienceEvent['action'],
			subject: T,
			attributes: UserExperiences[T] & CommonExperienceEventAttributes,
		) => {
			if (fg('ptc-enable-people-teams-ui-analytics-refactor')) {
				// @ts-ignore TODO: we may need to declare the keys to a const in the types file so we can do a check here
				fireEvent(`operational.${subject.replace(/[\/-]/g, '_')}.${action}`, {
					...attributes,
					source: 'clientMetrics',
				});
			} else {
				// @ts-ignore TODO: we may need to declare the keys to a const in the types file so we can do a check here
				DEPRECATED_fireEvent(`operational.${subject.replace(/[\/-]/g, '_')}.${action}`, {
					...attributes,
					source: 'clientMetrics',
				});
			}
		},
		[fireEvent, DEPRECATED_fireEvent],
	);

	const experienceTracker = useMemo(
		() => createTeamsExperienceTracker(fireExperienceEvent),
		[fireExperienceEvent],
	);

	return experienceTracker;
};
